import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/pro-regular-svg-icons";
import { BottomSheet } from "components/bottomSheetDialog";
import type { Selection } from "../domain/types";
import type { SettingsBreadcrumbSegment } from "./resolveSettingsBreadcrumb";
import type { SettingsSurfaceMeta } from "./settingsSurfaceMeta";

function settingsBreadcrumbSegmentKey(segment: SettingsBreadcrumbSegment): string {
  const { selection } = segment;
  switch (selection.kind) {
    case "email":
      return "email";
    case "group":
      return `group-${selection.groupId}`;
    case "row":
      return `row-${selection.rowId}`;
    case "column":
      return `column-${selection.rowId}-${selection.columnId}`;
    case "block":
      return `block-${selection.rowId}-${selection.columnId}-${selection.blockId}`;
    case "buttonRowSlot":
      return `button-row-slot-${selection.rowId}-${selection.columnId}-${selection.blockId}-${selection.slotIndex}`;
  }
}

type EmailBuilderSettingsSurfaceHeaderProps = {
  surfaceMeta: SettingsSurfaceMeta;
  breadcrumbSegments: SettingsBreadcrumbSegment[] | null;
  onSelect: (selection: Selection) => void;
  onClose?: () => void;
  className?: string;
  closeClassName?: string;
  showClose?: boolean;
};

export function EmailBuilderSettingsSurfaceHeader(
  props: EmailBuilderSettingsSurfaceHeaderProps,
): React.ReactElement {
  const { t } = useTranslation();
  const {
    surfaceMeta,
    breadcrumbSegments,
    onSelect,
    onClose,
    className,
    closeClassName,
    showClose = true,
  } = props;
  const parentSegments = breadcrumbSegments?.slice(0, -1) ?? [];

  const headerText = showClose ? (
    <>
      <h2 className="email-builder-settings-surface-title">{surfaceMeta.title}</h2>
      <p className="email-builder-settings-surface-description">{surfaceMeta.description}</p>
    </>
  ) : (
    <>
      <BottomSheet.Title className="email-builder-settings-surface-title">
        {surfaceMeta.title}
      </BottomSheet.Title>
      <BottomSheet.Description className="email-builder-settings-surface-description">
        {surfaceMeta.description}
      </BottomSheet.Description>
    </>
  );

  return (
    <div className={className}>
      <div className="email-builder-settings-surface-header-copy">
        {parentSegments.length > 0 ? (
          <nav
            className="email-builder-settings-surface-breadcrumb"
            aria-label={t("Settings navigation")}
          >
            {parentSegments.map((segment, index) => (
              <React.Fragment key={settingsBreadcrumbSegmentKey(segment)}>
                {index > 0 ? (
                  <span
                    className="email-builder-settings-surface-breadcrumb-separator"
                    aria-hidden="true"
                  >
                    /
                  </span>
                ) : null}
                <button
                  type="button"
                  className="email-builder-settings-surface-breadcrumb-link"
                  onClick={() => onSelect(segment.selection)}
                >
                  {segment.label}
                </button>
              </React.Fragment>
            ))}
          </nav>
        ) : null}
        {showClose ? (
          <div className="email-builder-settings-surface-header-title-row">
            <div className="email-builder-settings-surface-header-text">{headerText}</div>
            <button
              type="button"
              className={closeClassName}
              aria-label={t("Close settings")}
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faClose} aria-hidden="true" />
            </button>
          </div>
        ) : (
          <div className="email-builder-settings-surface-header-text">{headerText}</div>
        )}
      </div>
    </div>
  );
}
